import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useCachedFetch } from '@/composables/useFetch'
import { useSessionStore } from '@/stores/SessionStore'
import { useMapStore } from "@/stores/MapStore";
import { useUserStore } from "@/stores/UserStore";
import featuresJSON from "@/data/features.json";

// import useFeatures from "@/composables/useFeatures";
import useConfigurationGlobals from '@/composables/useConfigurationGlobals'
import useMapProductGlobals from '@/composables/useMapProductGlobals'

const { $BASEURL } = useConfigurationGlobals();
const { $ALLOWED_MAP_PRODUCTS, $DEFAULT_MAP_PRODUCT_SETTINGS, $PRODUCT_IDS } = useMapProductGlobals()

export let useProductStore = defineStore('productStore', () => {
  const { features } = featuresJSON;
  const sessionStore = useSessionStore();
  const userStore = useUserStore();
  const mapStore = useMapStore();

  const memberLevels = {
    '-1': 'guest',
    2: 'free',
    46: 'plus',
    49: 'pro',
    56: 'gold'
  }

  const defaultRegionalProducts = [ 1, 19, 2, 18, 3, 7, 12 ];

  const featureList = ref(features);
  const allowedProductIds = ref($ALLOWED_MAP_PRODUCTS);
  const productSettings = ref($DEFAULT_MAP_PRODUCT_SETTINGS);
  const availableProducts = ref([]);
  const availableCurrentModels = ref([]);
  const availableForecasts = ref([]);
  const customLayers = ref([]);

  async function getRegionalProducts(returnData = false) {
    const bounds = mapStore.getBounds();

    if (!bounds) return

    const { latMin, lonMin, latMax, lonMax } = bounds;

    const params = {
      units_wind: userStore.units.wind,
      units_temp: userStore.units.temp,
      units_distance: userStore.units.distance,
      units_precip: userStore.units.precip,
      min_lat: latMin,
      min_lon: lonMin,
      max_lat: latMax,
      max_lon: lonMax,
      wf_token: sessionStore.token
    }

    const queryString = new URLSearchParams(params).toString();
    const url = `${$BASEURL}/common/getRegionalProducts?${queryString}`;

    
    try {
      const { error, data, clear } = await useCachedFetch(url);

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        availableProducts.value = defaultRegionalProducts;
        if (typeof clear === 'function') clear();

        throw data.value.status || data.value.status_code;
      }

      availableProducts.value = data.value.available_regional_product_ids;
      availableCurrentModels.value = data.value.available_current_models;
      availableForecasts.value = data.value.available_forecasts;

      if (Object.prototype.hasOwnProperty.call(data.value, 'custom_layers')) {
        customLayers.value = data.value.custom_layers;
      }

      // Adds DSAT Operating Condition to available products
      // DSAT is not returned from getRegionalProducts even when the user has access to it
      if (allowedProductIds.value.includes('100')) {
        if (!availableProducts.value.includes(100)) availableProducts.value.push(100)
      }

      if (!returnData) return;

      return {
        availableProducts: availableProducts.value,
        availableCurrentModels: availableCurrentModels.value,
        availableForecasts: availableForecasts.value,
      }
    } catch (error) {
      console.warn(error);

      return error;
    }
  }
  
  const productList = computed(() => {
    const list = [];

    for (let productId of allowedProductIds.value) {
      const id = parseInt(productId);

      if (productId === null || productId === '') continue;

      const settings = Object.entries(productSettings.value);

      settings.forEach((setting) => {
        const settingId = parseInt(setting[0]);
        let productAvailable = availableProducts.value.includes(id);      

        if (settingId === id && (productAvailable || id === 100 || id === 25)) {
          setting[1].id = settingId
          list.push(setting[1])

          return
        }

        productAvailable = availableProducts.value.includes(setting[1].regionalProductId);

        if ( settingId === id && (productAvailable || setting[1].regionalProductId) ) {
          setting[1].id = settingId
          list.push(setting[1])
        }
      })
    }

    return list;
  });
  
  const allProductsList = computed(() => {
    const list = [];

    for (let productId of allowedProductIds.value) {
      const id = parseInt(productId);

      if (productId === null || productId === '') continue;

      const settings = productSettings.value[id]

      settings.id = id
      list.push(settings)
    }

    return list;
  });

  function productUpgradeLevels(productId) {
    const product = getProduct(productId);

    if (!product?.featureId) return [];

    const feature = featureList.value.find((feature) => {
      return feature.feature_id === product.featureId
    })

    const memberLevel = feature.use_non_trial_member_level
      ? userStore.nonTrialMemberLevel
      : userStore.memberLevel
 
    return feature.member_levels.filter(
      (level) => level > parseInt(memberLevel) && memberLevels[level]
    )
  };

  function hasFeatureUpgrade(featureId) {
    const feature = featureList.value.find((feature) => feature.feature_id === featureId);

    if (!feature) {
      console.warn('No feature found with the id of: ', featureId);
      return;
    }

    const memberLevel = feature.use_non_trial_member_level ? userStore.nonTrialMemberLevel : userStore.memberLevel;

    return feature.member_levels.indexOf(parseInt(memberLevel)) === -1
  }

  function hasProductUpgrade(productId) {
    const product = productList.value.find(product => {
      return product.id === productId
    })

    if (!product) {
      console.warn('No product found with the id of: ', productId);
      return false;
    }
    
    const featureId = product.featureId;

    if (!featureId) return false;

    return hasFeatureUpgrade(featureId);
  }

  function getProduct(productId) {
    return productList.value.find((product) => parseInt(product.id) === parseInt(productId));    
  }

  function getProductFromAllProductsList(productId) {
    return allProductsList.value.find((product) => parseInt(product.id) === parseInt(productId));    
  }

  function getProductIdByName(productName) {
    const ids = Object.keys(productSettings.value);
    let productId;

    ids.forEach(id => {
      if (productSettings.value[id].name !== productName) return;

      productId = id;
    });

    return productId
  }

  function getProductIdByOverlayName(overlayName) {
    // $PRODUCT_IDS uses uppercase with underscores for keys
    const convertedOverlayName = overlayName.replace(/-/g, '_').toUpperCase();
    let productId = null;

    if ($PRODUCT_IDS[convertedOverlayName]) productId = $PRODUCT_IDS[convertedOverlayName];

    return productId
  }

  function isProductAvailable(productId) {
    const id = parseInt(productId);
    const productIds = Object.values(availableProducts.value);
    
    return productIds.includes(id)
  }

  function isProductAvailableByName(name) {
    const id = getProductIdByName(name);

    return isProductAvailable(id);
  }

  function isProductAvailableByOverlayName(overlayName) {
    const id = getProductIdByOverlayName(overlayName);

    return isProductAvailable(id);
  }

  return {
    allowedProductIds,
    allProductsList,
    availableCurrentModels,
    availableForecasts,
    availableProducts,
    customLayers,
    featureList,
    memberLevels,
    productList,
    getProduct,
    getProductFromAllProductsList,
    getProductIdByName,
    getProductIdByOverlayName,
    getRegionalProducts,
    hasFeatureUpgrade,
    hasProductUpgrade,
    isProductAvailable,
    isProductAvailableByName,
    isProductAvailableByOverlayName,
    productUpgradeLevels,
  }
});
